import * as stylex from '@stylexjs/stylex';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useChildMatches, useNavigate, useRouter } from '@tanstack/react-router';
import { type HTMLAttributes, type Ref } from 'react';
import { Group, MenuTrigger, Pressable, TooltipTrigger } from 'react-aria-components';

import { Logo } from '@/components/logo.component';
import { SearchBar } from '@/components/search-bar.component';
import {
  Avatar,
  BookmarkStrokeIcon,
  HORIZONTAL_NAVIGATION_SLOTS,
  HorizontalNavigation,
  Menu,
  MenuItem,
  Popover,
  SettingsStrokeIcon,
  SignOutStrokeIcon,
  Text,
  Tooltip,
  useMediaQuery,
  RecentSearchesStrokeIcon,
  HelpCircleStrokeIcon,
  VerificationStrokeIcon,
  useScroll
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import { RouterIconButtonLink } from '@/libs/tanstack-router';
import { authMutations } from '@/resources/auth/auth.api';
import { profileQueries } from '@/resources/profile/profile.api';

interface HeaderProps extends Omit<HTMLAttributes<HTMLElement>, 'className' | 'style' | 'children'> {
  isPending?: boolean;
  children?: React.ReactNode;
  style?: stylex.StyleXStyles;
  ref?: Ref<HTMLElement>;
}

export function Header({ children, style, isPending, ...otherProps }: HeaderProps) {
  const isDesktop = useMediaQuery(breakpoints.large);
  const isTablet = useMediaQuery(breakpoints.medium);

  const navigate = useNavigate();
  const matches = useChildMatches();
  const router = useRouter();

  const currentRoute = matches.at(-1);

  const logoutMutation = useMutation(authMutations.logout);
  const profileQuery = useQuery(profileQueries.get);

  const [{ y: scrollY, direction }] = useScroll();
  const isScrollingDown = direction === 'down';
  const scrollThreshold = isDesktop ? 80 : 120;
  const isHeaderVisible = scrollY <= scrollThreshold || (!isScrollingDown && scrollY > scrollThreshold);

  return (
    <header
      {...otherProps}
      {...stylex.props(
        styles.base({ borderBottomWidth: children ? 1 : 0 }),
        isHeaderVisible ? styles.visible : styles.hidden,
        style)}>
      <HorizontalNavigation style={navigationStyles.base}>
        <Link to="/">
          <Logo />
        </Link>

        <Group slot={HORIZONTAL_NAVIGATION_SLOTS.CENTER}>
          {(isDesktop || isTablet) && <SearchBar />}
        </Group>

        <Group slot={HORIZONTAL_NAVIGATION_SLOTS.RIGHT}>
          {!isDesktop && !isTablet && <SearchBar isCompact />}

          <TooltipTrigger>
            <RouterIconButtonLink aria-label="Saved articles" size={32} to="/saved" variant="ghost">
              {(renderProps) => <BookmarkStrokeIcon color={renderProps.isActive ? 'interactive' : undefined} isFilled={renderProps.isActive} />}
            </RouterIconButtonLink>
            <Tooltip placement="left">Saved</Tooltip>
          </TooltipTrigger>

          <MenuTrigger>
            <Pressable>
              <Avatar role="button" size={32} style={avatarStyles.base}>
                {!isPending && profileQuery.data?.full_name?.[0]}
              </Avatar>
            </Pressable>
            <Popover offset={5} placement="bottom right">
              <Menu defaultSelectedKeys={currentRoute?.pathname ? [currentRoute.pathname.replace('/', '')] : undefined} selectionMode="single">
                <MenuItem
                  id="settings"
                  onAction={() => navigate({ to: '/settings' })}>
                  <SettingsStrokeIcon />
                  <Text>Settings</Text>
                </MenuItem>
                <MenuItem
                  id="history"
                  onAction={() => navigate({ to: '/history' })}>
                  <RecentSearchesStrokeIcon />
                  <Text>History</Text>
                </MenuItem>
                <MenuItem
                  id="help"
                  onAction={() => window.open('https://www.trendmd.com/support/', '_blank')}>
                  <HelpCircleStrokeIcon />
                  <Text>Help</Text>
                </MenuItem>
                <MenuItem
                  id="privacy"
                  onAction={() => window.open(router.buildLocation({ to: '/privacy-and-terms/$' }).pathname, '_blank')}>
                  <VerificationStrokeIcon />
                  <Text>Privacy and terms</Text>
                </MenuItem>
                <MenuItem
                  id="signOut"
                  onAction={() => logoutMutation.mutate(void 0, { onSettled: () => navigate({ to: '/sign-in' }) })}>
                  <SignOutStrokeIcon />
                  <Text>Sign out</Text>
                </MenuItem>
              </Menu>
            </Popover>
          </MenuTrigger>
        </Group>
      </HorizontalNavigation>
      {children}
    </header >
  );
}

const styles = stylex.create({
  base: (dynamicStyles: { borderBottomWidth: 1 | 0 }) => ({
    backgroundColor: $.surfaceBGPrimary,
    borderBottomColor: $.border06,
    borderBottomStyle: 'solid',
    borderBottomWidth: dynamicStyles.borderBottomWidth,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    left: 0,
    maxHeight: 201,
    minHeight: 60,
    position: 'fixed',
    transition: 'transform 0.3s ease-in-out, top 0.3s ease-in-out',
    width: '100%',
    zIndex: 3,
  }),
  visible: {
    top: 0,
    transform: 'translateY(0)',
  },
  hidden: {
    top: -201,
    transform: 'translateY(-100%)',
  }
});

const navigationStyles = stylex.create({
  base: {
    backgroundColor: $.surfaceBGPrimary,
    position: 'unset',
  }
});

const avatarStyles = stylex.create({
  base: {
    cursor: 'pointer',
  }
});
